import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import { ContentRoleLevel } from 'generated/graphql';
import { FC, useLayoutEffect, useRef, useState } from 'react';
import { Button, Loader, Tooltip } from '@spotted-zebra-uk/ui-components';
import DuplicateJobRoleFilters from './DuplicateJobRoleFilters';
import {
  TDuplicableJobRole,
  TDuplicateJobRoleFilters,
} from './DuplicateJobRoleModal.helpers';
import styles from './DuplicateJobRoleModal.module.scss';

type TDuplicateJobRoleResultsProps = {
  jobRoles: TDuplicableJobRole[];
  loading: boolean;
  count: number;
  selectedId?: number;
  onSelect: (role: TDuplicableJobRole) => void;
  searchValue: string;
  onSearch: (value: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  appliedFilters: TDuplicateJobRoleFilters;
  onApplyFilters: (filters: TDuplicateJobRoleFilters) => void;
  activeFilterCount: number;
  lockedRoleLevel?: ContentRoleLevel;
  activeLevelLabel?: string;
  itemsTotal?: number;
};

const DuplicateJobRoleResults: FC<TDuplicateJobRoleResultsProps> = ({
  jobRoles,
  loading,
  count,
  selectedId,
  onSelect,
  searchValue,
  onSearch,
  hasMore,
  onLoadMore,
  appliedFilters,
  onApplyFilters,
  activeFilterCount,
  lockedRoleLevel,
  activeLevelLabel,
  itemsTotal,
}) => {
  // Seed from the persisted search term so returning from step 2 (which
  // remounts this component) restores the term instead of blanking the input
  // while the query still holds it.
  const [inputValue, setInputValue] = useState(searchValue);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    onSearch(value);
  };

  const handleClearSearch = () => {
    setInputValue('');
    onSearch('');
  };

  const showEmptyState = !loading && count === 0;

  return (
    <div className={styles.step1}>
      <div className={styles.searchContainer}>
        <div className={styles.search}>
          <div className={styles.searchIconContainer}>
            <Icon icon="search" className={styles.searchIcon} />
          </div>
          <input
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Search company Role Skills Profiles by name…"
            className={styles.searchInput}
            data-testid="duplicate-job-role-modal__search"
          />
          {inputValue && (
            <button
              type="button"
              className={styles.searchClear}
              onClick={handleClearSearch}
              aria-label="Clear search"
              data-testid="duplicate-job-role-modal__search-clear"
            >
              <Icon icon="ic_close_gray" className={styles.searchClearIcon} />
            </button>
          )}
        </div>
        <Button
          variant="secondary"
          size="medium"
          rightIcon={<Icon icon="chevron" className={styles.filtersChevron} />}
          className={clsx(
            styles.filtersTrigger,
            isFiltersOpen && styles.filtersTriggerOpen,
          )}
          onClick={() => setIsFiltersOpen((open) => !open)}
          data-testid="duplicate-job-role-modal__filters-trigger"
        >
          Filters
          {activeFilterCount > 0 && (
            <span className={styles.filtersBadge}>{activeFilterCount}</span>
          )}
        </Button>
      </div>
      <div className={styles.step1Body}>
        <div
          className={clsx(
            styles.filtersPanel,
            isFiltersOpen && styles.filtersPanelOpen,
          )}
        >
          <DuplicateJobRoleFilters
            isOpen={isFiltersOpen}
            appliedFilters={appliedFilters}
            onApply={onApplyFilters}
            onClose={() => setIsFiltersOpen(false)}
            lockedRoleLevel={lockedRoleLevel}
          />
        </div>

        <div className={styles.step1Meta}>
          {activeLevelLabel && (
            <div className={styles.roleLevelChip}>
              {' '}
              Showing <span className={styles.bold}>
                {activeLevelLabel}
              </span>{' '}
              RSPs — duplicates inherit the level of this role's existing RSP
            </div>
          )}
          <span className={styles.countLabel}>
            {count === 1 ? '1 role' : `${count} roles`}{' '}
            {itemsTotal && `of ${itemsTotal} total`}
          </span>
        </div>

        <div
          className={styles.results}
          data-testid="duplicate-job-role-modal__results"
        >
          {loading ? (
            <div className={styles.loading}>
              <Loader size="small" /> Loading…
            </div>
          ) : showEmptyState ? (
            <div className={styles.resultsEmpty}>
              <Icon icon="search" className={styles.resultsEmptyIcon} />
              <p className={styles.resultsEmptyTitle}>
                No Role Skills Profiles match “{inputValue}”
              </p>
              <p className={styles.resultsEmptyHint}>
                Try a different name or adjust your filters.
              </p>
            </div>
          ) : (
            <>
              {jobRoles.map((role) => {
                const isSelected = role.id === selectedId;
                const meta = [
                  `Created ${role.createdLabel}`,
                  role.levelLabel,
                  role.familyLabel,
                ].filter(Boolean);

                return (
                  <button
                    key={role.id}
                    type="button"
                    disabled={role.disabled}
                    onClick={() => onSelect(role)}
                    data-testid="duplicate-job-role-modal__result"
                    className={clsx(
                      styles.resultCard,
                      isSelected && styles.resultCardSelected,
                      role.disabled && styles.resultCardDisabled,
                    )}
                  >
                    <span className={styles.resultInfo}>
                      <span className={styles.resultTitleRow}>
                        <span className={styles.resultName}>{role.name}</span>
                        <span className={styles.levelPill}>
                          {role.sourceLabel}
                        </span>
                      </span>
                      <span className={styles.resultMeta}>
                        {meta.map((part, index) => (
                          <span key={part}>
                            {index > 0 && (
                              <span className={styles.resultMetaDot}>•</span>
                            )}
                            {part}
                          </span>
                        ))}
                      </span>
                      {role.qualifier && (
                        <QualifierWithTooltip text={role.qualifier} />
                      )}
                    </span>
                    {role.disabled ? (
                      <span className={styles.resultStatusDisabled}>
                        No skills to display
                      </span>
                    ) : (
                      <span className={styles.resultStatus}>
                        {isSelected ? '✓' : '›'}
                      </span>
                    )}
                  </button>
                );
              })}

              {hasMore && (
                <div className={styles.loadMoreContainer}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={onLoadMore}
                    disabled={loading}
                    className={styles.loadMore}
                    data-testid="duplicate-job-role-modal__load-more"
                  >
                    {loading ? 'Loading…' : 'Load more'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuplicateJobRoleResults;

// Renders the qualifier as a single truncated line, revealing the full text in a
// tooltip only when it's actually cut off — a short note that fits gets no
// (redundant) tooltip.
const QualifierWithTooltip: FC<{ text: string }> = ({ text }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  return (
    <div className={styles.resultQualifierContainer}>
      <Tooltip content={text} disabled={!isTruncated}>
        <span ref={textRef} className={styles.resultQualifier}>
          {text}
        </span>
      </Tooltip>
    </div>
  );
};
